import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken, fileUrl } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  Loader2, Plus, Edit3, Trash2, Eye, EyeOff, X, Save, Upload, Package,
  ShoppingBag, Truck, AlertTriangle, ImagePlus, ImageOff, Clock,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const CATEGORIES = ["Parts", "Accessories", "Apparel", "Bundles", "Other"];
const AVAILABILITY_OPTIONS = [
  { key: "in_stock", label: "In stock — track inventory" },
  { key: "on_order", label: "Available on order — bypass stock" },
  { key: "discontinued", label: "Discontinued — hide from shop" },
];
const blankProduct = {
  sku: "", name: "", category: "Parts", description: "", price: "", cost: "",
  shipping_fee: "", stock: 0, low_stock_threshold: 3, track_stock: true,
  availability: "in_stock", lead_time_days: 7,
  images: [], active: true, sort_order: 100,
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const ProductForm = ({ initial, onSave, onClose, saving }) => {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), res.data] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (id) => setForm((f) => ({ ...f, images: (f.images || []).filter((i) => i.id !== id) }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.price) {
      toast.error("Name, description and price are required");
      return;
    }
    onSave({
      ...form,
      price: Number(form.price),
      cost: Number(form.cost || 0),
      shipping_fee: Number(form.shipping_fee || 0),
      stock: Number(form.stock || 0),
      low_stock_threshold: Number(form.low_stock_threshold || 3),
      lead_time_days: form.availability === "on_order" ? Number(form.lead_time_days || 7) : null,
      sort_order: Number(form.sort_order || 100),
    });
  };

  const isOnOrder = form.availability === "on_order";
  const isDiscontinued = form.availability === "discontinued";

  return (
    <div className="fixed inset-0 z-50 bg-spark-teal-night/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" data-testid="product-form-modal">
      <form onSubmit={submit} className="bg-spark-teal-night text-white w-full max-w-3xl rounded-xl border border-white/10 shadow-2xl my-8">
        <div className="bg-gradient-to-r from-spark-teal-deep to-spark-teal px-6 py-5 flex items-center justify-between rounded-t-xl border-b-4 border-spark-yellow">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">{form.id ? "Edit" : "New"} product</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold">{form.id ? form.name || "Edit product" : "Add a product"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-300 hover:text-white" data-testid="close-product-form"><X className="size-5" /></button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">SKU <span className="text-slate-500 normal-case tracking-normal font-normal">(optional · auto-generated if blank)</span></label>
              <input className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-spark-teal" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="auto · e.g. PRD-PAR-0001" data-testid="prod-sku" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Category</label>
              <select className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="prod-category">
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-spark-teal-night text-white">{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Name</label>
              <input className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Description</label>
              <textarea rows={4} className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-desc" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Price (NZD)</label>
              <input type="number" step="0.01" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Cost (NZD) <span className="text-slate-500 font-normal normal-case tracking-normal">— for margin tracking</span></label>
              <input type="number" step="0.01" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="prod-cost" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Shipping fee (NZD)</label>
              <input type="number" step="0.01" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.shipping_fee} onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })} data-testid="prod-shipping" />
              <p className="text-[10px] text-slate-500 mt-1">Charged per unit when customer chooses delivery. Pickup is always free.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Availability</label>
              <select className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="prod-availability">
                {AVAILABILITY_OPTIONS.map((o) => <option key={o.key} value={o.key} className="bg-spark-teal-night text-white">{o.label}</option>)}
              </select>
              {isOnOrder && (
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Lead time (days)</label>
                  <input type="number" min="1" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.lead_time_days || ""} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} data-testid="prod-leadtime" placeholder="e.g. 7" />
                  <p className="text-[10px] text-slate-500 mt-1">Customers will see "Available on order · ships in ~{form.lead_time_days || 7} days".</p>
                </div>
              )}
              {isDiscontinued && (
                <p className="text-[10px] text-orange-300 mt-2">Discontinued products are hidden from the public shop and can no longer be added to a cart.</p>
              )}
            </div>
            {!isOnOrder && !isDiscontinued && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Stock</label>
                  <input type="number" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="prod-stock" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Low-stock threshold</label>
                  <input type="number" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} data-testid="prod-threshold" />
                  <p className="text-[10px] text-slate-500 mt-1">Notify admin when stock falls to or below this number.</p>
                </div>
              </>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Sort order</label>
              <input type="number" className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-spark-teal" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="prod-sort" />
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-4 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="size-4 accent-spark-yellow" data-testid="prod-active" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-300">Visible to customers</span>
              </label>
              {!isOnOrder && !isDiscontinued && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.track_stock} onChange={(e) => setForm({ ...form, track_stock: e.target.checked })} className="size-4 accent-spark-yellow" data-testid="prod-track-stock" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-300">Track stock & alert when low</span>
                </label>
              )}
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-2 inline-block">Images</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {(form.images || []).map((img) => (
                <div key={img.id} className="relative aspect-square rounded-md overflow-hidden border border-white/10 group">
                  <img src={fileUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(img.id)} className="absolute top-1 right-1 size-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center" aria-label="Remove">
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
              <label className="aspect-square flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-white/15 rounded-md cursor-pointer text-slate-400 hover:text-spark-yellow hover:border-spark-yellow transition" data-testid="prod-image-upload">
                {uploading ? <Loader2 className="size-5 animate-spin" /> : <><ImagePlus className="size-5" /><span className="text-[10px] uppercase tracking-wider font-bold">Add image</span></>}
                <input type="file" accept="image/*" onChange={upload} className="hidden" disabled={uploading} />
              </label>
            </div>
          </div>
        </div>
        <div className="bg-spark-teal-deep/60 px-6 py-4 flex justify-end gap-2 rounded-b-xl border-t border-white/5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-slate-300 hover:text-white text-xs uppercase tracking-wider font-bold">Cancel</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-spark-yellow text-spark-ink hover:bg-spark-yellow-dark text-xs uppercase tracking-wider font-bold disabled:opacity-50" data-testid="save-product">
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {form.id ? "Save changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ProductsTab = ({ products, refresh }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const save = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.slug;
      delete payload.created_at; delete payload.updated_at;
      if (editing?.id) await api.patch(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing?.id ? "Product updated" : "Product created");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try { await api.patch(`/admin/products/${p.id}`, { active: !p.active }); refresh(); }
    catch { toast.error("Update failed"); }
  };

  const remove = (p) => setConfirmDelete(p);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/products/${confirmDelete.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={() => setEditing({ ...blankProduct })}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink hover:bg-spark-yellow-dark text-xs uppercase tracking-wider font-bold"
        data-testid="new-product-btn"
      >
        <Plus className="size-4" /> New product
      </button>

      {products.length === 0 ? (
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-16 text-center">
          <Package className="size-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-300 font-display uppercase tracking-wider">No products yet</p>
          <p className="text-sm text-slate-500 mt-1">Add parts, merch, or bundles for customers to buy.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {products.map((p) => {
            const isOnOrder = p.availability === "on_order";
            const isDiscontinued = p.availability === "discontinued";
            const lowStock = !isOnOrder && !isDiscontinued && p.track_stock && p.stock <= p.low_stock_threshold;
            const outOfStock = !isOnOrder && !isDiscontinued && p.track_stock && p.stock === 0;
            const badge = isDiscontinued
              ? { cls: "bg-slate-600 text-white", icon: <AlertTriangle className="size-3" />, text: "Discontinued" }
              : isOnOrder
                ? { cls: "bg-yellow-400 text-spark-ink", icon: <Clock className="size-3" />, text: `On order · ${p.lead_time_days || 7}d` }
                : lowStock
                  ? { cls: outOfStock ? "bg-red-500 text-white" : "bg-orange-500 text-white", icon: <AlertTriangle className="size-3" />, text: outOfStock ? "Out" : `Low · ${p.stock}` }
                  : null;
            return (
              <div key={p.id} className={`rounded-xl border ${p.active ? "border-white/[0.07]" : "border-white/[0.04] opacity-60"} bg-white/[0.04] overflow-hidden`} data-testid={`prod-${p.sku}`}>
                <div className="aspect-square bg-spark-teal-deep/40 relative">
                  {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0].storage_path)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                      <ImageOff className="size-8" />
                    </div>
                  )}
                  {badge && (
                    <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${badge.cls}`}>
                      {badge.icon} {badge.text}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-spark-yellow font-bold">{p.category}</p>
                  <p className="text-white font-semibold mt-0.5 line-clamp-1">{p.name}</p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{p.sku}</p>
                  <div className="mt-2 flex items-end justify-between">
                    <div>
                      <p className="font-display uppercase tracking-tight text-lg font-bold text-white">{money(p.price)}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">cost {money(p.cost)} · margin {money(p.price - p.cost)}</p>
                    </div>
                    {!isOnOrder && !isDiscontinued && p.track_stock && <p className="text-xs text-slate-300">{p.stock} in stock</p>}
                    {isOnOrder && <p className="text-xs text-yellow-300">On order</p>}
                  </div>
                </div>
                <div className="px-4 pb-3 flex items-center gap-1">
                  <button onClick={() => toggleActive(p)} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5" title={p.active ? "Hide" : "Show"} data-testid={`toggle-${p.sku}`}>
                    {p.active ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                  </button>
                  <button onClick={() => setEditing(p)} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5" title="Edit" data-testid={`edit-${p.sku}`}>
                    <Edit3 className="size-4" />
                  </button>
                  <button onClick={() => remove(p)} className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-white/5 ml-auto" title="Delete" data-testid={`delete-${p.sku}`}>
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <ProductForm initial={editing} onSave={save} onClose={() => setEditing(null)} saving={saving} />}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this product?"
        body={confirmDelete && (
          <>
            <p>You're about to permanently delete <strong className="text-white">{confirmDelete.name}</strong>{confirmDelete.sku ? <> ({confirmDelete.sku})</> : null}.</p>
            <p className="text-slate-400">This cannot be undone. The product will be removed from the public shop.</p>
          </>
        )}
        confirmLabel="Delete product"
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => !deleting && setConfirmDelete(null)}
        testId="confirm-delete-product"
      />
    </div>
  );
};

const OrdersTab = ({ orders, refresh }) => {
  const [filter, setFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/orders/${id}`, { status }); toast.success(`Marked ${status}`); refresh(); }
    catch { toast.error("Update failed"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${confirmDelete.id}`);
      toast.success(`Order ${confirmDelete.order_number} deleted`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {["all", "paid", "shipped", "completed", "refunded"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition ${filter === s ? "bg-spark-yellow text-spark-ink border-spark-yellow" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
            {s} <span className="ml-1.5 opacity-70">{s === "all" ? orders.length : orders.filter((o) => o.status === s).length}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-16 text-center">
          <ShoppingBag className="size-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-300 font-display uppercase tracking-wider">No orders yet</p>
          <p className="text-sm text-slate-500 mt-1">Stripe orders will arrive here once a customer pays.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((o) => (
            <li key={o.id} className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-4 sm:p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="font-display uppercase tracking-wider font-bold text-spark-yellow hover:text-yellow-300 underline-offset-2 hover:underline" data-testid={`open-order-${o.order_number}`}>
                    {o.order_number}
                  </button>
                  <p className="text-white text-sm mt-0.5">{o.customer_name} · {o.customer_email}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(o.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display uppercase tracking-tight text-2xl font-bold text-white">{money(o.total)}</p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-spark-yellow">{o.status}</p>
                  {o.refunded_amount > 0 && <p className="text-[10px] text-orange-300">−{money(o.refunded_amount)} refunded</p>}
                </div>
              </div>
              <ul className="mt-3 grid sm:grid-cols-2 gap-1 text-xs text-slate-300">
                {o.items.map((it, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span className="truncate">× {it.quantity}  {it.name}</span>
                    <span>{money(it.price * it.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 text-slate-400">
                  {o.fulfilment === "shipping" ? <Truck className="size-3.5" /> : <Package className="size-3.5" />}
                  {o.fulfilment === "shipping" ? `Shipping · ${o.shipping_address}` : "Pickup at workshop"}
                </span>
                {o.tracking_code && (
                  <span className="text-slate-300">
                    Tracking: <code className="bg-white/5 px-1.5 py-0.5 rounded">{o.tracking_code}</code>
                    {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="ml-1.5 text-spark-teal hover:underline">live →</a>}
                  </span>
                )}
                <div className="ml-auto flex flex-wrap gap-1.5">
                  <button onClick={() => navigate(`/admin/orders/${o.id}`)} className="px-2.5 py-1 rounded-md bg-white/10 text-white hover:bg-white/15 text-[10px] uppercase tracking-wider font-bold" data-testid={`manage-${o.order_number}`}>
                    Manage
                  </button>
                  {o.status === "paid" && o.fulfilment === "pickup" && (
                    <button onClick={() => setStatus(o.id, "completed")} className="px-2.5 py-1 rounded-md bg-spark-teal text-white hover:bg-spark-teal-dark text-[10px] uppercase tracking-wider font-bold">
                      Mark collected
                    </button>
                  )}
                  {o.status === "shipped" && (
                    <button onClick={() => setStatus(o.id, "completed")} className="px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-[10px] uppercase tracking-wider font-bold">
                      Mark delivered
                    </button>
                  )}
                  <button onClick={() => setConfirmDelete(o)} className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20" title="Delete order" data-testid={`delete-order-${o.order_number}`}>
                    <Trash2 className="size-3" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this order?"
        body={confirmDelete && (
          <>
            <p>You're about to permanently delete order <strong className="text-white">{confirmDelete.order_number}</strong> ({confirmDelete.customer_name}, {money(confirmDelete.total)}).</p>
            <p className="text-slate-400">This cannot be undone. If the customer was charged via Stripe, refund them first from the order page — deleting here only removes our record.</p>
          </>
        )}
        confirmLabel="Delete order"
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => !deleting && setConfirmDelete(null)}
        testId="confirm-delete-order"
      />
    </div>
  );
};

const AdminShop = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/orders"),
      ]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else toast.error("Failed to load shop");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <AdminLayout
      eyebrow="E-commerce"
      title="Shop"
    >
      <div className="space-y-5">
        <div className="flex gap-2 border-b border-white/5">
          {[
            { v: "products", label: "Products", count: products.length },
            { v: "orders", label: "Orders", count: orders.length },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2.5 -mb-px font-display uppercase tracking-wider text-sm font-bold border-b-2 transition-colors ${tab === t.v ? "text-spark-yellow border-spark-yellow" : "text-slate-400 border-transparent hover:text-white"}`}
              data-testid={`shop-tab-${t.v}`}
            >
              {t.label} <span className="text-xs ml-1.5 opacity-70">{t.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin inline" /></div>
        ) : tab === "products" ? (
          <ProductsTab products={products} refresh={load} />
        ) : (
          <OrdersTab orders={orders} refresh={load} />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminShop;
